<script lang="ts" module>
  import type { SystemStyleObject } from '@agapi/ui/panda/types';
  import type { ButtonProps } from '@agapi/ui/button';
  type MegaMenuMoreButtonProps = SystemStyleObject & { size: ButtonProps['size'] };

  export interface NavbarMegamenuProps {
    navData?: NavData;
  }
</script>

<script lang="ts">
  import { page } from '$app/state';

  import { getContext } from 'svelte';

  import { css } from '@agapi/ui/panda/css';
  import { Button } from '@agapi/ui/button';
  import {
    type NavbarStore,
    NavbarRow,
    NavbarGroup,
    NavbarMenuBar,
    NavbarItem,
    NavbarMegaMenu,
    NavbarMegaMenuColumn,
    NavbarMegaMenuHeader,
    NavbarMegaMenuLink,
    NavbarLinkWrapper
  } from '@agapi/ui/navbar';

  import { navbarMegamenuStyles } from '$storefront/components/navbar/_components/navbar-megamenu.styles';
  import type { NavData, NavItem } from '../../../types/navData';
  import { rewriteUrl } from '../../../utils';

  import * as m from '$lib/parachute/messages';

  const { navData }: NavbarMegamenuProps = $props();

  const navbar = getContext<NavbarStore>('navbar');

  const { megaMenuMoreButton, megaMenuColumn, megaMenuHeaderTitle, megaMenuLink } = navbarMegamenuStyles.raw();
  const { size: megamenuMoreButtonSize, ...megaMenuMoreButtonCss } = megaMenuMoreButton as MegaMenuMoreButtonProps;
</script>

<NavbarRow css={{ md: { position: 'relative', pb: 2, pt: 2 }, alignItems: 'flex-start' }}>
  <!-- Left Mega Menu -->
  {@render navItems(navData?.bottom_left_nav_items, "Left Mega Menu", "Categories")}

  <!-- Right Mega Menu -->
  {@render navItems(navData?.bottom_right_nav_items, 'Right Mega Menu', 'Miscellaneous')}
</NavbarRow>

{#snippet navItems(items: NavItem[] = [], _groupLabel: string, _menubarLabel: string)}
  <NavbarGroup label="Right Mega Menu" css={{ hideBelow: 'md', gap: 'unset' }}>
    <NavbarMenuBar label="Miscellaneous" css={{ flexWrap: 'wrap', flex: 1, rowGap: 'unset' }}>
      {#each items as category}
        {#if category.sub_items.length}
          <NavbarItem href={rewriteUrl(page, category.path)} label={category.label} rootStyles={{ mb: 4 }} labelStyles={css.raw({fontWeight: '600'})} enabled={!navbar?.isSearchOpen}>
            <NavbarMegaMenu colorVariant="primary.subdued">
              {#each category?.sub_items ?? [] as subCategory}
                <NavbarMegaMenuColumn css={megaMenuColumn}>
                  <NavbarMegaMenuHeader title={subCategory.label} titleStyles={megaMenuHeaderTitle} href={rewriteUrl(page, subCategory.path)} />
                  {#each subCategory?.sub_items?.slice(0, 4) ?? [] as item}
                    <NavbarMegaMenuLink href={rewriteUrl(page, item.path)} css={megaMenuLink} label={item.label} />
                  {/each}
                  {#if subCategory?.sub_items?.length > 4}
                    <NavbarLinkWrapper>
                      <Button
                        as="a"
                        href={rewriteUrl(page, subCategory.path)}
                        variant="text"
                        size={megamenuMoreButtonSize}
                        css={megaMenuMoreButtonCss}
                      >
                        {#if subCategory.label === 'Varumärke'}
                          {m.showAll({ value: '' })}
                        {:else}
                          {m.showAll({ value: '' /* subCategory?.sub_items?.length */ })}
                        {/if}
                      </Button>
                    </NavbarLinkWrapper>
                  {/if}
                </NavbarMegaMenuColumn>
              {/each}
            </NavbarMegaMenu>
          </NavbarItem>
        {:else}
          <!--
            TODO: Check with svelte community if this is a bug or not or if there is a better solution, children?.length is always one if we use if statement etc...
            This is an issue with svelte, because we use an if statement inside Item above it will always have a child element,
            so we need to add an empty Item here to fix the issue as we do not want to render sub items behavior with CaretDown/CaretUp
          -->
          <NavbarItem href={rewriteUrl(page, category.path)} label={category.label} rootStyles={{ mb: 4 }} labelStyles={css.raw({fontWeight: '600'})} enabled={!navbar?.isSearchOpen} />
        {/if}
      {/each}
    </NavbarMenuBar>
  </NavbarGroup>
{/snippet}
